import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, Users, Package, HandHeart } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { toast } from 'sonner';

const ClaimStorePicker = () => {
  const nav = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [catFilter, setCatFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/onboarding/claimable');
        setStores(data.stores || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load claimable stores');
      } finally { setLoading(false); }
    })();
  }, []);

  const cats = ['all', ...Array.from(new Set(stores.map((s) => s.category)))];
  const visible = catFilter === 'all' ? stores : stores.filter((s) => s.category === catFilter);

  const claim = async (vendor_id) => {
    setClaiming(vendor_id);
    try {
      const { data } = await api.post('/onboarding/claim', { vendor_id });
      if (data.is_additional) {
        toast.success(`${data.vendor.name} added to your stores — awaiting super-admin approval. Switch to it from the store switcher.`);
        setStores((prev) => prev.map((s) => s.id === vendor_id
          ? { ...s, claim_count: (s.claim_count || 0) + 1, already_claimed_by_me: true } : s));
        setClaiming(null);
      } else {
        toast.success(`${data.vendor.name} is yours — awaiting super-admin approval!`);
        nav(data.redirect || '/vendor-admin');
        window.location.reload();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not claim store');
      setClaiming(null);
    }
  };

  return (
    <div data-testid="claim-store-picker">
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <HandHeart className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-900">Claim a ready-made store</div>
            <p className="text-sm text-slate-500 mt-0.5">
              AI-built stores with a full product catalog. Claiming gives you your own copy —
              your products, your prices. The same store can be claimed by multiple vendors.
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="claim-category-filter">
          {cats.map((c) => (
            <button key={c} onClick={() => setCatFilter(c)}
              className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${catFilter === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
              data-testid={`claim-cat-${String(c).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-btn`}>
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-80 bg-white border border-slate-200 rounded-2xl animate-pulse" />
        ))}
        {!loading && visible.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500 text-sm" data-testid="claim-empty-state">
            No claimable stores yet — check back soon.
          </div>
        )}
        {visible.map((s) => (
          <article key={s.id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`claimable-card-${s.id}`}>
            <div className="h-32 relative overflow-hidden bg-slate-100">
              {s.cover ? (
                <img src={s.cover} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${s.color_primary || '#10b981'}, ${s.color_secondary || '#0f172a'})` }} />
              )}
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/90 text-[10px] font-semibold text-slate-800 uppercase tracking-wider">{s.category}</span>
              {s.claim_count > 0 && (
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-slate-900/80 text-[10px] font-medium text-white inline-flex items-center gap-1" data-testid={`claim-count-${s.id}`}>
                  <Users className="h-3 w-3" /> Claimed by {s.claim_count}
                </span>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-slate-900 leading-tight">{s.name}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{s.tagline}</p>
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
                <Package className="h-3 w-3" />
                <span>{s.item_count} products across {s.sections_count} sections</span>
              </div>
              <Button onClick={() => claim(s.id)} disabled={claiming != null || s.already_claimed_by_me}
                className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                data-testid={`claim-store-${s.id}-btn`}>
                {claiming === s.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-1.5" />}
                {s.already_claimed_by_me ? 'Already claimed' : 'Claim this store'}
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ClaimStorePicker;
